import { useState } from 'react'
import { motion } from 'framer-motion'
import { UserPlus, Users, CheckCircle, AlertCircle } from 'lucide-react'
import { PageHeader, useAuthStore, fadeUp } from '@personic/shared'
import { buildBreadcrumbs } from '@/config/routes'
import { useTeamMembers, useInvitations, useInviteTeamMember, useRemoveTeamMember, useUpdateMemberRole, useCancelInvitation } from '@/hooks/useTeam'
import TeamMemberTable from '@/components/tenant/TeamMemberTable'
import InviteModal from '@/components/tenant/InviteModal'

export default function Members() {
  const { user } = useAuthStore()
  const tenantId = user?.tenant_id || ''
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  const { data: members, isLoading: membersLoading } = useTeamMembers(tenantId)
  const { data: invitations } = useInvitations(tenantId)
  const inviteMember = useInviteTeamMember(tenantId)
  const removeMember = useRemoveTeamMember(tenantId)
  const updateRole = useUpdateMemberRole(tenantId)
  const cancelInvite = useCancelInvitation(tenantId)

  const breadcrumbs = buildBreadcrumbs('/settings/team/members')

  const handleInvite = async (email: string, role: string) => {
    try {
      await inviteMember.mutateAsync({ email, role })
      setSuccessMsg(`Invitation sent to ${email}`)
      setShowInviteModal(false)
      setTimeout(() => setSuccessMsg(''), 5000)
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.detail || 'Failed to send invitation')
      setTimeout(() => setErrorMsg(''), 5000)
    }
  }

  return (
    <div>
      <PageHeader
        title="Team Members"
        description="Manage your team, invite new members, and control access."
        breadcrumbs={breadcrumbs}
        action={
          <button
            onClick={() => setShowInviteModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-copper-500 text-white text-sm font-medium hover:bg-copper-600 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            Invite Member
          </button>
        }
      />

      {successMsg && (
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-sage-50 border border-sage-200 text-sage-700 text-sm">
          <CheckCircle className="w-4 h-4" />
          {successMsg}
        </motion.div>
      )}

      {errorMsg && (
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4" />
          {errorMsg}
        </motion.div>
      )}

      <motion.div variants={fadeUp} initial="hidden" animate="visible" className="rounded-xl border border-warm-200 bg-white p-6 max-w-5xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-copper-50 flex items-center justify-center">
            <Users className="w-5 h-5 text-copper-500" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-midnight-800">Active Members</h2>
            <p className="text-xs text-warm-500">{members?.length ?? 0} member{(members?.length ?? 0) !== 1 ? 's' : ''}</p>
          </div>
        </div>

        {membersLoading ? (
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-12 bg-warm-100 rounded" />
            ))}
          </div>
        ) : (
          <TeamMemberTable
            members={members || []}
            invitations={invitations || []}
            onRemoveMember={(userId: string) => removeMember.mutate(userId)}
            onUpdateRole={(userId: string, role: string) => updateRole.mutate({ userId, role })}
            onCancelInvitation={(invitationId: string) => cancelInvite.mutate(invitationId)}
            currentUserId={user?.id || ''}
            isUpdatingRole={updateRole.isPending}
            isRemoving={removeMember.isPending}
          />
        )}
      </motion.div>

      {/* Role Info */}
      <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mt-6 rounded-xl border border-warm-200 bg-white p-6 max-w-5xl">
        <h3 className="font-heading font-semibold text-midnight-800 mb-3">Role Descriptions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div className="p-3 rounded-lg bg-warm-50">
            <p className="font-medium text-midnight-800">Admin</p>
            <p className="text-warm-500 mt-1">Full access to all tenant features, billing, team management, and API keys.</p>
          </div>
          <div className="p-3 rounded-lg bg-warm-50">
            <p className="font-medium text-midnight-800">Member</p>
            <p className="text-warm-500 mt-1">Access to core features like profiles, engage, and personas. Cannot manage billing or team.</p>
          </div>
        </div>
      </motion.div>

      {showInviteModal && (
        <InviteModal
          onInvite={handleInvite}
          onClose={() => setShowInviteModal(false)}
          isLoading={inviteMember.isPending}
        />
      )}
    </div>
  )
}
